import { useColorMode } from '@chakra-ui/react';
import { type ColorKey, colorScales, semanticColors } from '@main/complykit/tokens/colors';
import { useMemo } from 'react';

import { type StatusTone, toneFromColorScheme } from '../legacy-color-scheme';

export type StatusChartColors = {
  /** The soft area/bar/pie fill; legend swatches use the same value. */
  fill: string;
  /** On-fill text, measured against `fill` (see the step table below). */
  label: string;
  /** The solid step 9, for lines and thin strokes only. */
  stroke: string;
};

/* Charts and chips resolve from the same ComplyKit scales, so a Valid chip
 * and a Valid slice are the same hue in both modes. Legacy Chakra schemes
 * bridge through the same tone map the chip engines use, so a bridged chip
 * and its slice cannot disagree either.
 *
 * The steps, each chosen by measurement (WCAG ratios; dark page ground
 * approximated as neutral dark step 1 #111111, cards sit on #191919):
 *
 *   fill    the scale's SOFT range as a solid, opaque value: light step 4,
 *           the visual weight of the old Chakra .100 pastels and the chip
 *           soft backgrounds (1.19:1 info to 1.28:1 critical against white).
 *           Dark mode takes step 5: dark step 4 measures as low as 1.28:1
 *           against the page and ~1.2:1 against a card, too faint to read
 *           as a filled mark, while step 5 sits at 1.45 to 1.83:1 (1.35 to
 *           1.71:1 on cards) and still reads as a tint. Solid sub-scale
 *           steps rather than alpha: chart fills must be opaque so stacked
 *           and overlapping marks cannot blend.
 *   label   the scale's own dark text instead of white, which soft fills
 *           cannot carry. Step 11 clears 4.5:1 only in dark mode (5.03
 *           info to 6.27 neutral on the step 5 fills); on the light step 4
 *           fills it measures 3.54 warning to 4.83 neutral, failing AA for
 *           four of the five tones, so light mode takes step 12 (9.11
 *           warning to 13.30 neutral).
 *   stroke  the solid step 9 in both modes, what the Status dot and solid
 *           chip fill render. Lines and thin strokes keep it: a 2px stroke
 *           at the soft steps disappears into the page, thin marks need
 *           the weight the fills gave up.
 *
 * The values are literal hex resolved per color mode rather than css vars:
 * the charts are SVG, but they paint through presentation attributes
 * (fill=/stroke= on rect, path and text), where var() does not resolve. */
const TONE_CHART_SCALE: Record<StatusTone, ColorKey> = {
  neutral: 'neutral',
  info: semanticColors.info,
  success: semanticColors.success,
  warning: semanticColors.warning,
  critical: semanticColors.error,
  ai: semanticColors.ai,
};

const FILL_STEP = { light: 4, dark: 5 } as const;
const LABEL_STEP = { light: 12, dark: 11 } as const;
const STROKE_STEP = 9;

function isStatusTone(value: string): value is StatusTone {
  return value in TONE_CHART_SCALE;
}

/**
 * The mode-resolved chart trio for a named ComplyKit scale. This is the
 * palette door for the rare NON-status series (a categorical line, a purple
 * total): the caller commits to a scale, never to a hex value.
 */
export function getChartScaleColors(scaleKey: ColorKey, isDark: boolean): StatusChartColors {
  const steps = isDark ? colorScales[scaleKey].dark : colorScales[scaleKey].regular;

  return {
    fill: steps[isDark ? FILL_STEP.dark : FILL_STEP.light] ?? '',
    label: steps[isDark ? LABEL_STEP.dark : LABEL_STEP.light] ?? '',
    stroke: steps[STROKE_STEP] ?? '',
  };
}

/**
 * The tone behind a chart series color, whether the series committed to a
 * tone already or still names a legacy Chakra scheme. Chips rendered next to
 * chart marks resolve through this so they cannot disagree with the fills,
 * which bridge through the same map.
 */
export function toneFromChartColor(colorSchemeOrTone: string): StatusTone {
  return isStatusTone(colorSchemeOrTone)
    ? colorSchemeOrTone
    : toneFromColorScheme(colorSchemeOrTone);
}

function getChartColors(colorSchemeOrTone: string, isDark: boolean): StatusChartColors {
  return getChartScaleColors(TONE_CHART_SCALE[toneFromChartColor(colorSchemeOrTone)], isDark);
}

function useIsDarkMode(): boolean {
  const { colorMode } = useColorMode();
  return colorMode === 'dark';
}

export function useStatusTagChartColors(colorSchemeOrTone: string): StatusChartColors {
  const isDark = useIsDarkMode();

  return useMemo(() => getChartColors(colorSchemeOrTone, isDark), [colorSchemeOrTone, isDark]);
}

export function useStatusTagChartColorsByKey<T extends string>(
  rawEntries: readonly { key: T; tone: StatusTone }[],
): Record<T, StatusChartColors> {
  const isDark = useIsDarkMode();

  return useMemo(
    () =>
      rawEntries.reduce(
        (acc, { key, tone }) => {
          acc[key] = getChartColors(tone, isDark);
          return acc;
        },
        {} as Record<T, StatusChartColors>,
      ),
    [isDark, rawEntries],
  );
}

export function useStatusTagChartFillColors(colorSchemesOrTones: readonly string[]): string[] {
  const isDark = useIsDarkMode();

  return useMemo(
    () => colorSchemesOrTones.map((value) => getChartColors(value, isDark).fill),
    [colorSchemesOrTones, isDark],
  );
}

/** `getChartScaleColors` as a hook, for a single non-status series. */
export function useChartScaleColors(scaleKey: ColorKey): StatusChartColors {
  const isDark = useIsDarkMode();

  return useMemo(() => getChartScaleColors(scaleKey, isDark), [isDark, scaleKey]);
}

/**
 * Step 9 strokes for a categorical set of line series, one per scale.
 * Strokes rather than fills on purpose: the categorical need so far is
 * multi-series line charts, and thin strokes keep the solid step.
 */
export function useChartScaleStrokeColors(scaleKeys: readonly ColorKey[]): string[] {
  const isDark = useIsDarkMode();

  return useMemo(
    () => scaleKeys.map((scaleKey) => getChartScaleColors(scaleKey, isDark).stroke),
    [isDark, scaleKeys],
  );
}

export type ChartStructureColors = {
  /** The empty remainder behind an hbar or progress fill. */
  track: string;
  /** Grid and radial guide lines. */
  grid: string;
  /** Axis and dimension label text drawn in SVG. */
  axisLabel: string;
  /** Axis tick strokes. */
  tick: string;
};

/* The achromatic chart furniture, all on the neutral scale, each step the
 * closest palette mapping of the hex the charts used to hardcode:
 * track light 2 / dark 4 (was #fafcfd / Chakra gray-700), grid light 3 /
 * dark 6 (was #F2F4F7, which dark mode never overrode), axis label light 10
 * / dark 11 (was #718096 / #CBD5E0), tick step 8 in both modes (was
 * #A0AEC0 / #4A5568). */
const STRUCTURE_STEPS = {
  track: { light: 2, dark: 4 },
  grid: { light: 3, dark: 6 },
  axisLabel: { light: 10, dark: 11 },
  tick: { light: 8, dark: 8 },
} as const;

export function useChartStructureColors(): ChartStructureColors {
  const isDark = useIsDarkMode();

  return useMemo(() => {
    const steps = isDark ? colorScales.neutral.dark : colorScales.neutral.regular;
    const mode = isDark ? 'dark' : 'light';

    return {
      track: steps[STRUCTURE_STEPS.track[mode]] ?? '',
      grid: steps[STRUCTURE_STEPS.grid[mode]] ?? '',
      axisLabel: steps[STRUCTURE_STEPS.axisLabel[mode]] ?? '',
      tick: steps[STRUCTURE_STEPS.tick[mode]] ?? '',
    };
  }, [isDark]);
}
